import * as EventConstants from 'constants/EventConstants';
import * as context from 'Context';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';
import {
  FromServerTemplateVersion,
  TemplateVersion,
  ToServerTemplateVersion,
} from 'notebook/entry-templates/TemplateConstants';
import VersionedItemSubstore, {ItemEditorState, ItemVersionState} from 'notebook/store/VersionedItemSubstore';
import {removeQueryResolutionsForTableFromSnapshotInNote} from 'notebook/tables/excess/Resolver';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import * as WorkflowEntryTemplateUtil from 'workflows/entry-templates/WorkflowEntryTemplateUtil';

import * as WorkflowEntryTemplateActions from './WorkflowEntryTemplateActions';
import WorkflowEntryTemplateUpdater from './WorkflowEntryTemplateUpdater';

// Store for a collection of StageEntryTemplates
class WorkflowEntryTemplateStore extends Store {
  itemSubstore!: VersionedItemSubstore<
    WorkflowEntryTemplateUtil.WorkflowEntryTemplate,
    NotePart,
    WorkflowEntryTemplateUtil.FromServerWorkflowEntryTemplate,
    FromServerTemplateVersion,
    'entryTemplate',
    'entryTemplateId'
  >;

  resetState(): void {
    if (this.itemSubstore == null) {
      this.itemSubstore = new VersionedItemSubstore<
        WorkflowEntryTemplateUtil.WorkflowEntryTemplate,
        NotePart,
        WorkflowEntryTemplateUtil.FromServerWorkflowEntryTemplate,
        FromServerTemplateVersion,
        'entryTemplate',
        'entryTemplateId'
      >({
        options: {
          idKey: 'entryTemplateId',
          itemKey: 'entryTemplate',
          getVersionCid: (item: WorkflowEntryTemplateUtil.WorkflowEntryTemplate) => item.templateVersion.cid,
          asyncItemLoad: true,
          sanitizeItem: WorkflowEntryTemplateUtil.sanitizeTemplate,
          postSanitizeItem: this.postSanitizeItem.bind(this),
          prepareForStore: WorkflowEntryTemplateUtil.prepareForStore,
          pickRevertableFields: WorkflowEntryTemplateUtil.pickRevertableFields,
          doSave: null, // Auto-save disabled
        },
        events: {
          item: {
            load: {
              init: EventConstants.WORKFLOW_ENTRY_TEMPLATE_FETCH_STARTED,
              completed: EventConstants.WORKFLOW_ENTRY_TEMPLATE_FETCH_COMPLETED,
              failed: EventConstants.WORKFLOW_ENTRY_TEMPLATE_FETCH_FAILED,
            },
            close: EventConstants.WORKFLOW_ENTRY_TEMPLATE_DISCARD,
            revert: 'N/A',
            update: EventConstants.WORKFLOW_ENTRY_TEMPLATE_UPDATE,
            registerObjects: EventConstants.WORKFLOW_ENTRY_TEMPLATE_REGISTER_OBJECTS,
            save: {
              init: EventConstants.WORKFLOW_ENTRY_TEMPLATE_SAVE_STARTED,
              completed: EventConstants.WORKFLOW_ENTRY_TEMPLATE_SAVE_COMPLETED,
              failed: EventConstants.WORKFLOW_ENTRY_TEMPLATE_SAVE_FAILED,
            },
          },
        },
      });
    }
    this.itemSubstore.resetState();
  }

  postSanitizeItem(
    id: string,
    newWorkflowEntryTemplate: WorkflowEntryTemplateUtil.WorkflowEntryTemplate,
    oldWorkflowEntryTemplate: WorkflowEntryTemplateUtil.WorkflowEntryTemplate | null,
    {bumpVersion, performTableEvaluation = true}: {bumpVersion: boolean; performTableEvaluation: boolean}
  ): WorkflowEntryTemplateUtil.WorkflowEntryTemplate {
    if (!performTableEvaluation) {
      return newWorkflowEntryTemplate;
    }

    const updater = new WorkflowEntryTemplateUpdater(
      id,
      () => this.itemSubstore.getItem(id),
      (templateVersion: TemplateVersion) => {
        // TODO: We should change WorkflowEntryTemplate to auto-save.
        const {name} = this.itemSubstore.getItemUnsafe(id);
        WorkflowEntryTemplateActions.updateNote(id, templateVersion.entryVersion.note, name, {
          bumpVersion,
        });
      }
    );
    return updater.run(newWorkflowEntryTemplate, oldWorkflowEntryTemplate);
  }

  inStore(id: string): boolean {
    return this.itemSubstore.inStore(id);
  }

  getEditorState(
    id: string
  ): ItemEditorState<
    WorkflowEntryTemplateUtil.WorkflowEntryTemplate,
    NotePart,
    'entryTemplate',
    'entryTemplateId'
  > {
    return this.itemSubstore.getEditorState(id);
  }

  getVersionState(id: string): ItemVersionState<FromServerTemplateVersion> {
    return this.itemSubstore.getVersionState(id);
  }

  getIdFromCid(id: string, cid: string): string | number | undefined {
    return this.itemSubstore.getIdFromCid(id, cid);
  }

  hasUnsavedChanges(id: string): boolean {
    return this.itemSubstore.hasUnsavedChanges(id);
  }

  payloadHandler(action: FluxAction): boolean {
    if (action.actionType === EventConstants.EXCESS_DELETE_SNAPSHOTTED_RESOLUTIONS_FOR_TABLE) {
      const {itemId, provenanceUuid, lookupConfigIds} = action;
      if (!this.inStore(itemId)) {
        return true;
      }

      const workflowEntryTemplate = this.itemSubstore.getItemUnsafe(itemId);
      const newWorkflowEntryTemplate: WorkflowEntryTemplateUtil.WorkflowEntryTemplate = {
        ...workflowEntryTemplate,
        templateVersion: {
          ...workflowEntryTemplate.templateVersion,
          entryVersion: {
            ...workflowEntryTemplate.templateVersion.entryVersion,
            note: removeQueryResolutionsForTableFromSnapshotInNote(
              workflowEntryTemplate.templateVersion.entryVersion.note,
              provenanceUuid,
              lookupConfigIds
            ),
          },
        },
      };

      this.itemSubstore.updateItem(itemId, newWorkflowEntryTemplate, {bumpVersion: true});
      this.emitChange();
      return true;
    }

    if (action.actionType === EventConstants.EXCESS_FORCE_EVALUATION) {
      const {fileId} = action;
      // If the entry isn't in the store anymore we can skip evaluation.
      if (!this.inStore(fileId)) {
        return true;
      }

      const {entryTemplate} = this.itemSubstore.getItemStateUnsafe(fileId);

      const updater = new WorkflowEntryTemplateUpdater(
        fileId,
        () => this.itemSubstore.getItem(fileId),
        (templateVersion: TemplateVersion) => {
          // TODO: We should change WorkflowEntryTemplate to auto-save.
          const {name} = this.itemSubstore.getItemUnsafe(fileId);
          WorkflowEntryTemplateActions.updateNote(fileId, templateVersion.entryVersion.note, name, {
            bumpVersion: false,
          });
        }
      );
      context.using({forceExcessRevaluate: true}, this, () => updater.run(entryTemplate, entryTemplate));

      this.emitChange();
      return true;
    }

    // @ts-expect-error ts(2345)
    const shouldEmit = this.itemSubstore.handlePayload(action);
    if (shouldEmit) {
      this.emitChange();
    }
    return true;
  }

  prepareForSave(
    id: string,
    entryTemplate: WorkflowEntryTemplateUtil.WorkflowEntryTemplate
  ):
    | {templateVersion: ToServerTemplateVersion; name: string}
    | {templateVersion: ToServerTemplateVersion; entryTemplateName: string} {
    const {cidIdMap} = this.itemSubstore.getItemStateUnsafe(id);
    return WorkflowEntryTemplateUtil.prepareForSave(entryTemplate, cidIdMap);
  }
}

const entryTemplateStore = new WorkflowEntryTemplateStore(AppDispatcher);
export default entryTemplateStore;
